import { noop } from 'lodash';
import { useResizeObserver } from '@ardoq/hooks';
import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { TabButton } from './TabButton';
import {
  TabsButtonWrapperWithChevrons,
  TabsButtonWrapperWithDropdown,
} from './TabsButtonWrapper';
import { OverflowType, TabsGroupProps } from './types';

const TabsGroupHeaderWrapper = styled.div<{
  $overflowType: OverflowType;
  $hasRightContent: boolean;
}>`
  display: flex;
  flex-direction: column;
  ${({ $overflowType }) =>
    $overflowType === 'chevrons' &&
    css`
      flex: 0;
      width: 0;
      min-width: 100%;
    `}
  ${({ $overflowType }) =>
    $overflowType === 'dropdown' &&
    css`
      flex: 1;
    `}
  ${({ $hasRightContent }) =>
    $hasRightContent &&
    css`
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    `}
`;

export const TABS_GROUP_HEADER_SELECTOR = TabsGroupHeaderWrapper.toString();

export const TabBody = styled.div`
  overflow: auto;
`;

export const TAB_BODY_SELECTOR = TabBody.toString();

type TabsGroupHeaderProps<T extends string = string> = TabsGroupProps<T> & {
  activeTabId?: string;
  setActiveTabId: (activeTabId: T) => void;
};

export const TabsGroupHeader = <T extends string = string>({
  align,
  children,
  onTabChange = noop,
  overflowType = 'chevrons',
  activeTabId,
  setActiveTabId,
  extraDropdownOptions,
  rightContent,
  clickNamespace,
  fullButtons,
  moreButtonLabel,
}: TabsGroupHeaderProps<T>) => {
  const rootWrapperRef = useRef(null);
  const { width: rootWrapperWidth = 0 } = useResizeObserver(rootWrapperRef);
  const TabsButtonWrapper =
    overflowType === 'chevrons'
      ? TabsButtonWrapperWithChevrons
      : TabsButtonWrapperWithDropdown;

  const handleTabChange = (newTabId: string) => {
    setActiveTabId(newTabId as T);
    onTabChange(newTabId as T);
  };

  return (
    <TabsGroupHeaderWrapper
      ref={rootWrapperRef}
      $overflowType={overflowType}
      $hasRightContent={!!rightContent}
      data-click-namespace={clickNamespace}
    >
      <TabsButtonWrapper
        align={align}
        activeTabId={activeTabId}
        onTabChange={handleTabChange}
        rootWrapperWidth={rootWrapperWidth}
        extraDropdownOptions={extraDropdownOptions}
        clickNamespace={clickNamespace}
        fullButtons={fullButtons}
        moreButtonLabel={moreButtonLabel}
      >
        {children.map(child => (
          <TabButton
            key={child.props.tabId}
            {...child.props}
            isActive={activeTabId === child.props.tabId}
            onClick={() => handleTabChange(child.props.tabId)}
          />
        ))}
      </TabsButtonWrapper>
      {rightContent && <div>{rightContent}</div>}
    </TabsGroupHeaderWrapper>
  );
};

type SetActiveTabAction<T extends string = string> = [
  T | undefined,
  (tabId: T) => void,
];

export const TabsGroup = <T extends string = string>(
  props: TabsGroupProps<T>
) => {
  const key = props.children.map(child => child.props.label).join('|');
  const isControlledComponent = 'activeTabId' in props;

  const defaultActiveTab =
    props.defaultActiveTabId || props.children[0].props.tabId;

  const internalStateHandle = useState<T>(defaultActiveTab);

  const [rawActiveTabId, setActiveTabId]: SetActiveTabAction<T> =
    isControlledComponent ? [props.activeTabId, noop] : internalStateHandle;

  // Ensure activeTabId points to a valid tab, fallback to default tab if not found
  const activeTabId =
    rawActiveTabId &&
    props.children.some(child => child.props.tabId === rawActiveTabId)
      ? rawActiveTabId
      : defaultActiveTab;

  const activeChild = props.children.find(
    child => child.props.tabId === activeTabId
  );

  return (
    <>
      <TabsGroupHeader
        key={key}
        {...props}
        activeTabId={activeTabId}
        setActiveTabId={setActiveTabId}
      />
      {activeChild && activeChild.props.children && (
        <TabBody>{activeChild.props.children}</TabBody>
      )}
    </>
  );
};
